import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Toaster, toast } from 'sonner';
import { 
  CheckCircle2, ArrowRight, ArrowLeft, User, Briefcase, 
  Monitor, FileText, Send
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Compétences commerciales à évaluer
const commercialSkills = [
  { id: 'prospect', label: 'Prospecter de nouveaux clients (téléphone, email, LinkedIn)' },
  { id: 'discovery', label: 'Conduire un entretien de découverte des besoins' },
  { id: 'proposal', label: 'Élaborer et présenter une proposition commerciale' },
  { id: 'negotiate', label: 'Négocier les conditions d\'une vente' },
  { id: 'crm', label: 'Utiliser un outil CRM (HubSpot, Salesforce ou équivalent)' },
  { id: 'kpi', label: 'Analyser ses résultats commerciaux (KPI, tableau de bord)' },
  { id: 'report', label: 'Rédiger un compte-rendu de visite ou rapport d\'activité' },
  { id: 'watch', label: 'Réaliser une veille concurrentielle et sectorielle' }
];

// Compétences numériques à évaluer
const digitalSkills = [
  { id: 'office', label: 'Suite Office (Word, Excel, PowerPoint)' },
  { id: 'linkedin', label: 'LinkedIn et réseaux sociaux professionnels' },
  { id: 'email', label: 'Outils de messagerie professionnelle' },
  { id: 'data', label: 'Tableur et analyse de données' }
];

const skillLevels = [
  { value: 'none', label: 'Aucune expérience', color: 'bg-gray-200' },
  { value: 'partial', label: 'Expérience partielle', color: 'bg-[#C8A75A]/50' },
  { value: 'mastery', label: 'Maîtrise', color: 'bg-[#C8A75A]' }
];

const digitalLevels = [
  { value: 'beginner', label: 'Débutant', color: 'bg-gray-200' },
  { value: 'intermediate', label: 'Intermédiaire', color: 'bg-[#C8A75A]/50' },
  { value: 'advanced', label: 'Avancé', color: 'bg-[#C8A75A]' }
];

export const Positionnement = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Identification
    lastName: '',
    firstName: '',
    birthDate: '',
    email: '',
    phone: '',
    educationLevel: '',
    lastExperience: '',
    professionalProject: '',
    sourceInfo: '',
    // Compétences commerciales
    commercialSkills: {},
    // Compétences numériques
    digitalSkills: {}
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSkillChange = (skillType, skillId, value) => {
    setFormData(prev => ({
      ...prev,
      [skillType]: {
        ...prev[skillType],
        [skillId]: value
      }
    }));
  };

  const nextStep = () => {
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/positionnement`, formData);
      toast.success('Fiche de positionnement envoyée !', {
        description: 'Nous analyserons votre profil et vous recontacterons.'
      });
      setStep(5); // Success step
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="positionnement-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero */}
      <section className="bg-[#0B1F3A] py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
            Espace Étudiant
          </span>
          <h1 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            Fiche de positionnement
          </h1>
          <p className="text-[#F5F5F5]/80">
            Évaluez vos compétences pour définir votre parcours personnalisé
          </p>
        </div>
      </section>

      {/* Progress Bar */}
      {step < 5 && (
        <section className="bg-white border-b border-[#E6E6E6] py-6">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-4">
              {[
                { num: 1, label: 'Identification', icon: User },
                { num: 2, label: 'Compétences commerciales', icon: Briefcase },
                { num: 3, label: 'Compétences numériques', icon: Monitor },
                { num: 4, label: 'Validation', icon: FileText }
              ].map((s, index) => (
                <div key={s.num} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    step >= s.num ? 'bg-[#0B1F3A] text-white' : 'bg-[#E6E6E6] text-[#4A4A4A]'
                  }`}>
                    {step > s.num ? <CheckCircle2 size={20} /> : s.num}
                  </div>
                  <span className={`ml-2 text-sm hidden md:inline ${
                    step >= s.num ? 'text-[#0B1F3A] font-medium' : 'text-[#4A4A4A]'
                  }`}>
                    {s.label}
                  </span>
                  {index < 3 && (
                    <div className={`w-8 md:w-16 h-0.5 mx-2 ${
                      step > s.num ? 'bg-[#C8A75A]' : 'bg-[#E6E6E6]'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Form Content */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          
          {/* Step 1: Identification */}
          {step === 1 && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="step-1">
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-6">
                Identification du candidat
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Nom *</Label>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    required
                    data-testid="input-lastname"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Prénom *</Label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    required
                    data-testid="input-firstname"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Date de naissance *</Label>
                  <Input
                    name="birthDate"
                    type="date"
                    value={formData.birthDate}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    required
                    data-testid="input-birthdate"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Téléphone *</Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    required
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Niveau de formation *</Label>
                  <Select onValueChange={(value) => handleSelectChange('educationLevel', value)} value={formData.educationLevel}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="select-education">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cap-bep">CAP/BEP</SelectItem>
                      <SelectItem value="bac">Baccalauréat</SelectItem>
                      <SelectItem value="bac+1">Bac+1</SelectItem>
                      <SelectItem value="bac+2">Bac+2</SelectItem>
                      <SelectItem value="bac+3">Bac+3 ou plus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Dernière expérience professionnelle</Label>
                  <Textarea
                    name="lastExperience"
                    value={formData.lastExperience}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    rows={3}
                    placeholder="Poste, entreprise, durée..."
                    data-testid="textarea-experience"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Projet professionnel visé *</Label>
                  <Textarea
                    name="professionalProject"
                    value={formData.professionalProject}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    rows={3}
                    placeholder="Quel métier visez-vous après la formation ?"
                    required
                    data-testid="textarea-project"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Comment avez-vous connu Institut BIA ?</Label>
                  <Select onValueChange={(value) => handleSelectChange('sourceInfo', value)} value={formData.sourceInfo}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="select-source">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internet">Recherche internet</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="recommendation">Bouche à oreille</SelectItem>
                      <SelectItem value="parcoursup">Parcoursup</SelectItem>
                      <SelectItem value="salon">Salon / Forum</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <Button onClick={nextStep} className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none px-8" data-testid="next-step-1">
                  Suivant <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Commercial Skills */}
          {step === 2 && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="step-2">
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-2">
                Compétences commerciales
              </h2>
              <p className="text-[#4A4A4A] mb-8">
                Évaluez votre niveau pour chaque compétence commerciale
              </p>

              <div className="space-y-6">
                {commercialSkills.map((skill) => (
                  <div key={skill.id} className="border border-[#E6E6E6] p-4">
                    <Label className="text-sm font-medium text-[#0B1F3A] block mb-3">
                      {skill.label}
                    </Label>
                    <RadioGroup
                      value={formData.commercialSkills[skill.id] || ''}
                      onValueChange={(value) => handleSkillChange('commercialSkills', skill.id, value)}
                      className="flex flex-wrap gap-4"
                    >
                      {skillLevels.map((level) => (
                        <div key={level.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={level.value} id={`${skill.id}-${level.value}`} />
                          <Label htmlFor={`${skill.id}-${level.value}`} className="text-sm cursor-pointer">
                            {level.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-between">
                <Button onClick={prevStep} variant="outline" className="rounded-none px-8" data-testid="prev-step-2">
                  <ArrowLeft className="mr-2" size={18} /> Précédent
                </Button>
                <Button onClick={nextStep} className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none px-8" data-testid="next-step-2">
                  Suivant <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Digital Skills */}
          {step === 3 && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="step-3">
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-2">
                Compétences numériques
              </h2>
              <p className="text-[#4A4A4A] mb-8">
                Évaluez votre niveau pour chaque outil numérique
              </p>

              <div className="space-y-6">
                {digitalSkills.map((skill) => (
                  <div key={skill.id} className="border border-[#E6E6E6] p-4">
                    <Label className="text-sm font-medium text-[#0B1F3A] block mb-3">
                      {skill.label}
                    </Label>
                    <RadioGroup
                      value={formData.digitalSkills[skill.id] || ''}
                      onValueChange={(value) => handleSkillChange('digitalSkills', skill.id, value)}
                      className="flex flex-wrap gap-4"
                    >
                      {digitalLevels.map((level) => (
                        <div key={level.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={level.value} id={`${skill.id}-${level.value}`} />
                          <Label htmlFor={`${skill.id}-${level.value}`} className="text-sm cursor-pointer">
                            {level.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-between">
                <Button onClick={prevStep} variant="outline" className="rounded-none px-8" data-testid="prev-step-3">
                  <ArrowLeft className="mr-2" size={18} /> Précédent
                </Button>
                <Button onClick={nextStep} className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none px-8" data-testid="next-step-3">
                  Suivant <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Summary & Submit */}
          {step === 4 && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="step-4">
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-6">
                Récapitulatif
              </h2>

              <div className="space-y-6">
                {/* Identity Summary */}
                <div className="border border-[#E6E6E6] p-4">
                  <h3 className="font-semibold text-[#0B1F3A] mb-3 flex items-center gap-2">
                    <User size={18} className="text-[#C8A75A]" /> Identification
                  </h3>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <p><span className="text-[#4A4A4A]">Nom :</span> {formData.lastName} {formData.firstName}</p>
                    <p><span className="text-[#4A4A4A]">Email :</span> {formData.email}</p>
                    <p><span className="text-[#4A4A4A]">Téléphone :</span> {formData.phone}</p>
                    <p><span className="text-[#4A4A4A]">Niveau :</span> {formData.educationLevel}</p>
                  </div>
                </div>

                {/* Commercial Skills Summary */}
                <div className="border border-[#E6E6E6] p-4">
                  <h3 className="font-semibold text-[#0B1F3A] mb-3 flex items-center gap-2">
                    <Briefcase size={18} className="text-[#C8A75A]" /> Compétences commerciales
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(formData.commercialSkills).map(([key, value]) => {
                      const skill = commercialSkills.find(s => s.id === key);
                      const level = skillLevels.find(l => l.value === value);
                      return skill && level ? (
                        <span key={key} className={`text-xs px-2 py-1 ${level.color} text-[#0B1F3A]`}>
                          {skill.label.split(' ')[0]}: {level.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* Digital Skills Summary */}
                <div className="border border-[#E6E6E6] p-4">
                  <h3 className="font-semibold text-[#0B1F3A] mb-3 flex items-center gap-2">
                    <Monitor size={18} className="text-[#C8A75A]" /> Compétences numériques
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(formData.digitalSkills).map(([key, value]) => {
                      const skill = digitalSkills.find(s => s.id === key);
                      const level = digitalLevels.find(l => l.value === value);
                      return skill && level ? (
                        <span key={key} className={`text-xs px-2 py-1 ${level.color} text-[#0B1F3A]`}>
                          {skill.label.split(' ')[0]}: {level.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-between">
                <Button onClick={prevStep} variant="outline" className="rounded-none px-8" data-testid="prev-step-4">
                  <ArrowLeft className="mr-2" size={18} /> Précédent
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 font-semibold"
                  data-testid="submit-positionnement"
                >
                  {isSubmitting ? 'Envoi...' : 'Envoyer ma fiche'}
                  <Send className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && (
            <div className="bg-white p-12 border border-[#E6E6E6] text-center" data-testid="step-success">
              <div className="w-20 h-20 bg-[#C8A75A] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-[#0B1F3A]" size={40} />
              </div>
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-4">
                Fiche envoyée avec succès !
              </h2>
              <p className="text-[#4A4A4A] mb-8">
                Notre équipe pédagogique analysera votre profil et vous contactera 
                pour définir ensemble votre parcours personnalisé.
              </p>
              <a href="/espace-etudiant">
                <Button className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none px-8">
                  Retour à l'espace étudiant
                </Button>
              </a>
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default Positionnement;
